import {
  citationsAsText,
  footnotesHtml,
  withoutCitations,
} from "./citations.js";
import { documentDetails, documentType } from "./fileCard.js";
import { ATTACHMENT_REFERENCE, bareAsNodes } from "./references.js";
import { isTableMime, tableCardMarkdown } from "./tableCard.js";

/**
 * A card's markdown as it reads outside Oliver: each table reference becomes the rows
 * its card shows, and a document its name, type and size. Any other reference becomes its
 * name, never an `attachment:` link. With
 * sources, each cited group (a table's cells too) becomes its numbers, its sources listed
 * under it; without, no citation is left at all.
 */
export function plainTextForCopy(
  content,
  artifacts,
  citations,
  { withSources = false } = {}
) {
  const { resolvedAttachments, tables } = artifacts.state;
  let hasTables = false;
  const text = bareAsNodes(content).replace(
    ATTACHMENT_REFERENCE,
    (_match, _bang, label, ref) => {
      const id = artifacts.attachmentIdFor(ref);
      const resolved = id ? resolvedAttachments[id] : null;
      const name = label || resolved?.filename || "download";
      const fileType = resolved?.url ? documentType(resolved) : null;
      if (fileType) {
        return `${resolved.filename || name} (${documentDetails(
          fileType,
          resolved.size
        ).replace(" · ", ", ")})`;
      }
      const table = tables[id];
      if (!isTableMime(resolved?.mimeType) || !table || table.failed) {
        return name;
      }
      hasTables = true;
      const marks =
        withSources && citations
          ? (tokens) => {
              const cited = citations.forTable(id, tokens);
              return cited.length
                ? `[${cited.map(({ token }) => token).join(", ")}]`
                : "";
            }
          : null;
      return tableCardMarkdown(name, table, marks);
    }
  );
  const plain = hasTables ? text.replace(/\n{3,}/g, "\n\n").trim() : text;
  return withSources
    ? citationsAsText(plain, citations)
    : withoutCitations(plain);
}

/**
 * A card's rendered HTML made ready for the clipboard: no controls, a document as one line of
 * its name, type and size (never its link, which expires), and the inline borders a table
 * needs to show a grid in Word, since clipboard fragments carry no CSS. With sources,
 * each source is its number with the sources as footnotes; without, no citation is left.
 */
export function htmlForCopy(html, citations, { withSources = false } = {}) {
  const container = document.createElement("div");
  container.innerHTML = html;
  const numbers = [];
  if (!withSources) {
    container
      .querySelectorAll(
        ".chart-caption, .table-card__source, .file-card__footer"
      )
      .forEach((el) => el.remove());
  }
  // A chart's or table's source line, copied with sources, is its numbers.
  container.querySelectorAll("[data-sources]").forEach((line) => {
    const ns = line.dataset.sources.split(",").map(Number);
    numbers.push(...ns);
    const sup = document.createElement("sup");
    sup.textContent = ns.map((n) => `[${n}]`).join("");
    line.replaceWith(sup);
  });
  container.querySelectorAll(".citation-marker").forEach((marker) => {
    if (!withSources) {
      const before = marker.previousSibling;
      if (before?.nodeType === Node.TEXT_NODE) {
        before.textContent = before.textContent.replace(/\s+$/, "");
      }
      marker.remove();
      return;
    }
    const n = Number(marker.dataset.citation);
    numbers.push(n);
    const sup = document.createElement("sup");
    sup.textContent = `[${n}]`;
    marker.replaceWith(sup);
  });
  container.querySelectorAll(".file-card").forEach((card) => {
    const shown = card.querySelector(".file-card__name")?.textContent;
    if (!shown) {
      card.remove();
      return;
    }
    const line = document.createElement("p");
    const name = document.createElement("strong");
    name.textContent = shown;
    // A card that failed shows why, which is no part of the answer.
    const details = card.classList.contains("file-card--failed")
      ? null
      : card.querySelector(".file-card__details")?.textContent;
    line.append(name, details ? ` (${details.replace(" · ", ", ")})` : "");
    // A copied source line is already its numbers.
    card.querySelectorAll("sup").forEach((sup) => line.append(sup));
    card.replaceWith(line);
  });
  container
    .querySelectorAll(
      ".chart-actions, .table-card__footer, .table-card__expand"
    )
    .forEach((el) => el.remove());
  container.querySelectorAll("table").forEach((table) => {
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
  });
  container.querySelectorAll("th, td").forEach((cell) => {
    cell.style.border = "1px solid #cccccc";
    cell.style.padding = "8px 12px";
    cell.style.textAlign = "left";
  });
  container.querySelectorAll("th").forEach((cell) => {
    cell.style.background = "#f2f2f2";
    cell.style.fontWeight = "600";
  });
  container
    .querySelectorAll(".table-card tbody tr:nth-child(even) td")
    .forEach((cell) => {
      cell.style.background = "#fafafa";
    });
  return withSources
    ? container.innerHTML + footnotesHtml(citations, numbers)
    : container.innerHTML;
}
